/**
 * Morosidad de cartera: antigüedad de saldos, categorías de riesgo con su
 * reserva (provisión) y cartera en riesgo (PAR).
 */
import { BASIS_POINTS_PER_UNIT } from '../money/money.js';
/** Rangos de antigüedad de saldos que usan los reportes de cartera y cobranza. */
export const AGING_BUCKETS = [
    { id: 'current', label: 'Al día', minDays: 0, maxDays: 0 },
    { id: 'd1_30', label: '1–30 días', minDays: 1, maxDays: 30 },
    { id: 'd31_60', label: '31–60 días', minDays: 31, maxDays: 60 },
    { id: 'd61_90', label: '61–90 días', minDays: 61, maxDays: 90 },
    { id: 'd91_180', label: '91–180 días', minDays: 91, maxDays: 180 },
    { id: 'd181_plus', label: 'Más de 180 días', minDays: 181, maxDays: Number.POSITIVE_INFINITY },
];
/** Rango de respaldo cuando ningún otro coincide (el más atrasado). */
const WORST_AGING_BUCKET = 'd181_plus';
/** Rango de antigüedad al que pertenece un préstamo según sus días de atraso. */
export function agingBucket(daysPastDue) {
    const bucket = AGING_BUCKETS.find((b) => daysPastDue >= b.minDays && daysPastDue <= b.maxDays);
    return bucket?.id ?? WORST_AGING_BUCKET;
}
/**
 * Categorías de riesgo y tasas de reserva para microcrédito, según el
 * Reglamento para la Administración del Riesgo de Crédito (JM-93-2005, Junta
 * Monetaria). Las entidades no supervisadas por la SIB las usan como buena
 * práctica; las tasas son configuración de la organización para que cada
 * entidad se ajuste a su propia política o regulador.
 */
export const RISK_CATEGORIES = ['A', 'B', 'C', 'D', 'E'];
/** Categoría de un préstamo sin atraso (la de todo préstamo nuevo). */
export const NORMAL_RISK_CATEGORY = 'A';
/**
 * Días de atraso que en la práctica equivalen a "sin límite" para la
 * categoría E (100 años); se usa un número finito para que la regla sea
 * serializable en la configuración.
 */
const UNBOUNDED_DAYS_PAST_DUE = 36_500;
/** Tabla por defecto de categorías de riesgo (JM-93-2005). */
export const DEFAULT_RISK_CATEGORY_RULES = [
    { category: 'A', label: 'Normal', maxDaysPastDue: 30, provisionRate: 0 },
    { category: 'B', label: 'Con atraso', maxDaysPastDue: 60, provisionRate: 500 },
    { category: 'C', label: 'Con atraso significativo', maxDaysPastDue: 90, provisionRate: 2000 },
    { category: 'D', label: 'Alto riesgo de irrecuperabilidad', maxDaysPastDue: 180, provisionRate: 5000 },
    { category: 'E', label: 'Irrecuperable', maxDaysPastDue: UNBOUNDED_DAYS_PAST_DUE, provisionRate: 10000 },
];
/** Categoría de riesgo que corresponde a los días de atraso (la peor si ninguna aplica). */
export function riskCategoryFor(daysPastDue, rules = DEFAULT_RISK_CATEGORY_RULES) {
    const sorted = [...rules].sort((a, b) => a.maxDaysPastDue - b.maxDaysPastDue);
    const worst = sorted.at(-1);
    if (!worst)
        throw new RangeError('Se requiere al menos una categoría de riesgo');
    return sorted.find((r) => daysPastDue <= r.maxDaysPastDue) ?? worst;
}
/** Umbral por defecto de la cartera en riesgo: PAR30, la métrica principal de la industria. */
export const DEFAULT_PAR_THRESHOLD_DAYS = 30;
/**
 * Cartera en riesgo (PAR): proporción del capital pendiente que pertenece a
 * préstamos con más de `thresholdDays` días de atraso. Devuelve puntos básicos.
 */
export function portfolioAtRisk(loans, thresholdDays = DEFAULT_PAR_THRESHOLD_DAYS) {
    const total = loans.reduce((sum, l) => sum + l.outstandingPrincipal, 0);
    if (total === 0)
        return 0;
    const atRisk = loans.filter((l) => l.daysPastDue > thresholdDays).reduce((sum, l) => sum + l.outstandingPrincipal, 0);
    return Math.round((atRisk / total) * BASIS_POINTS_PER_UNIT);
}
//# sourceMappingURL=delinquency.js.map